import React from 'react';
import { Separator } from '@frontend/shadcn/components/ui/separator';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import Text from '@frontend/app/refresh-components/Text';
import { getCurrencySymbol } from '../../pages/LandingPage/utils/LandingPagePreviewUtils';
import { formatValue, DataFormat } from '../../utils';
import { getLocaleString } from '../../utils/dateTimeUtils';

interface PayoutLineItem {
  id: string;
  name: string;
  payout: number;
  __typename?: string;
}

interface PayoutRuleData {
  ruleTitle: string;
  totalPayout: number;
  lineItems: PayoutLineItem[] | null;
  orderPlacedAt: Date;
  orderSubtotal: number;
  currency?: string;
  flatPayout?: number;
  percentPayout?: number;
  __typename?: string;
}

interface PayoutLogsResponse {
  getOfferPayoutLogs: PayoutRuleData[];
}

export function PayoutCalculationDialog({ data, payoutLabel, currency }: {
  data: PayoutLogsResponse,
  payoutLabel?: string,
  currency?: string
}): JSX.Element {
  const currencySymbol = getCurrencySymbol(currency || 'USD');
  const payoutLogs = data?.getOfferPayoutLogs || [];

  return (
    <Card className="w-[362px] border-none bg-white">
      <CardContent className="flex flex-col gap-2 p-0">
        <Text visualVariant="paragraph" className="text-grey-6 font-medium">
          How payout is calculated?
        </Text>

        <div className="flex flex-col gap-2">
          <div className="w-full bg-grey-2 rounded-lg py-1 px-2">
            <Text visualVariant="caption" className="text-grey-6">
              Payout Applied: {payoutLabel || 'N/A'}
            </Text>
          </div>

          <Separator className="bg-grey-2" />

          <div className="flex flex-col gap-2">
            {/* Headers */}
            <div className="grid grid-cols-12 gap-2">
              <Text visualVariant="caption" className="text-grey-6 font-medium col-span-4">Applied On</Text>
              <Text visualVariant="caption" className="text-grey-6 font-medium col-span-3">Amount</Text>
              <Text visualVariant="caption" className="text-grey-6 font-medium col-span-2">Payout</Text>
              <Text visualVariant="caption" className="text-grey-6 font-medium col-span-3">Earned</Text>
            </div>

            {/* Row Data */}
            {payoutLogs.map((log, index) => (
              <div key={index} className="grid grid-cols-12 gap-2">
                <Text visualVariant="caption" className="text-grey-5 col-span-4">
                  {log.ruleTitle}
                  {log.ruleTitle === 'DURATION' ? (
                    <span className="text-grey-5 px-1">
                      (Order Placed At - {getLocaleString(log.orderPlacedAt)})
                    </span>
                  ) : (
                    log.lineItems?.map((item) => (
                      <div key={item.id}>
                        {item.name.split(',').map((name) => (
                          <div key={name}>
                            ({name})
                          </div>
                        ))}
                      </div>
                    ))
                  )}
                </Text>
                <Text visualVariant="caption" className="text-grey-5 col-span-3">
                  ${formatValue(DataFormat.Money, log.orderSubtotal)}
                </Text>
                <Text visualVariant="caption" className="text-grey-5 col-span-2">
                  {log.flatPayout > 0 ? `$${formatValue(DataFormat.Money, log.flatPayout)}` :
                   log.percentPayout > 0 ? `${log.percentPayout}%` : '0'}
                </Text>
                <Text visualVariant="caption" className="text-grey-5 col-span-3">
                  {currencySymbol}{formatValue(DataFormat.Money, log.totalPayout)}
                </Text>
              </div>
            ))}
          </div>

          <Separator className="bg-grey-2" />

          <div className="flex justify-between items-center">
            <Text visualVariant="paragraph" className="text-grey-6 font-medium">
              Payout Earned
            </Text>
            <Text visualVariant="paragraph" className="text-grey-6 font-medium px-4">
              {currencySymbol}{formatValue(DataFormat.Money,
                payoutLogs.reduce((sum, log) => sum + (log.totalPayout || 0), 0)
              )}
            </Text>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
